import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Check, X, Lock, FileText, Send, Euro, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Compact admin-only dropdown to manually change quote/invoice status.
// Backend endpoints: PATCH /api/quotes/{id}/status  +  PATCH /api/invoices/{id}/status
// Required props: type ('quote' | 'invoice'), id, currentStatus, axiosInstance, onUpdated()

const QUOTE_OPTIONS = [
  { value: 'goedgekeurd', label: 'Goedgekeurd (akkoord)', icon: Check, color: 'text-green-600' },
  { value: 'afgewezen', label: 'Afgewezen', icon: X, color: 'text-red-600', confirm: 'Reden van afwijzing (optioneel):' },
  { value: 'gesloten', label: 'Gesloten', icon: Lock, color: 'text-gray-600' },
  { value: 'sent', label: 'Verstuurd (reset)', icon: Send, color: 'text-blue-600' },
  { value: 'draft', label: 'Concept (reset)', icon: FileText, color: 'text-gray-500' },
];

const INVOICE_OPTIONS = [
  { value: 'betaald', label: 'Betaald', icon: Euro, color: 'text-green-600' },
  { value: 'afgewezen', label: 'Afgewezen', icon: X, color: 'text-red-600', confirm: 'Reden van afwijzing (optioneel):' },
  { value: 'gesloten', label: 'Gesloten', icon: Lock, color: 'text-gray-600' },
  { value: 'sent', label: 'Verstuurd (reset)', icon: Send, color: 'text-blue-600' },
  { value: 'open', label: 'Open (reset)', icon: FileText, color: 'text-gray-500' },
];

const statusLabel = (status) => {
  const map = {
    draft: 'Concept', concept: 'Concept', sent: 'Verstuurd', open: 'Open',
    goedgekeurd: 'Akkoord', afgewezen: 'Afgewezen', gesloten: 'Gesloten',
    betaald: 'Betaald', overdue: 'Te laat', converted: 'Omgezet',
  };
  return map[status] || status || '-';
};

export const StatusActionDropdown = ({ type, id, currentStatus, axiosInstance, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const options = type === 'invoice' ? INVOICE_OPTIONS : QUOTE_OPTIONS;
  const endpoint = type === 'invoice' ? `/invoices/${id}/status` : `/quotes/${id}/status`;

  const apply = async (opt) => {
    if (opt.value === currentStatus) {
      toast.info(`Status is al "${statusLabel(opt.value)}"`);
      return;
    }
    let note = null;
    if (opt.confirm) {
      note = window.prompt(opt.confirm, '');
      if (note === null) return;  // user cancelled
    } else if (!window.confirm(`Status wijzigen naar "${statusLabel(opt.value)}"?`)) {
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.patch(endpoint, { status: opt.value, note: note || undefined });
      toast.success(`Status gewijzigd naar "${statusLabel(opt.value)}"`);
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wijzigen mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={loading}
          className="text-amber-700 hover:bg-amber-50"
          title="Status handmatig wijzigen"
          data-testid={`status-action-${type}-${id}`}
        >
          {loading
            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            : <Lock className="w-4 h-4 mr-1" />}
          <span className="text-xs">Status</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[11px] text-gray-500">
          Huidige: {statusLabel(currentStatus)}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => {
          const Icon = opt.icon;
          return (
            <DropdownMenuItem
              key={opt.value}
              onClick={() => apply(opt)}
              className={`cursor-pointer ${opt.color}`}
              data-testid={`status-action-${type}-${id}-${opt.value}`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default StatusActionDropdown;
